import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import { getPayslipEmployees, getPayslips, previewPayslip, sendPayslip } from '../../lib/api';
import type { PayslipEmployee, PayslipEmployeeType, PayslipInput, PayslipRecord } from '../../lib/api';

const fmt = (n: number) => `₹${n.toLocaleString('en-IN')}`;
const monthTitle = (m: string) => new Date(`${m}-01T00:00:00`).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
const num = (v: string) => Math.max(0, Number(v) || 0);
const rupees = (v: string) => Math.round(num(v));

// Early in the month the admin is usually paying for the month just gone.
function defaultMonth(): string {
  const d = new Date();
  if (d.getDate() <= 10) d.setMonth(d.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

interface FormState {
  employeeType: PayslipEmployeeType;
  employeeId: string;
  email: string;
  month: string;
  basicDa: string;
  hra: string;
  incentive: string;      // salespeople only
  totalDays: string;
  leaveDays: string;
  esic: string;
  epfo: string;
  deductLeave: boolean;   // unticked: leave days show on the slip, nothing is taken off
}

// Incentive and Deduct Leave are decided per slip, so they aren't carried over.
const EMPTY_SALARY = { basicDa: '', hra: '0', incentive: '0', esic: '0', epfo: '0', deductLeave: false };

const INPUT_CLS = 'w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400';

const Field: React.FC<{ label: string; hint?: React.ReactNode; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs font-bold text-purple-800 uppercase tracking-wider pt-1">{children}</p>
);

const TypeBadge: React.FC<{ type: PayslipEmployeeType }> = ({ type }) => (
  <span className="text-[11px] px-2 py-0.5 rounded-md font-semibold"
    style={type === 'mentor' ? { background: '#ede9fe', color: '#5b21b6' } : { background: '#dbeafe', color: '#1d4ed8' }}>
    {type === 'mentor' ? 'Mentor' : 'Sales'}
  </span>
);

const fmtLeaveDate = (d: { date: string; days: number }) =>
  new Date(`${d.date}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) + (d.days === 0.5 ? ' (½)' : '');

// The selected person's approved leave for the pay month (Admin → Leave
// Requests), plus a warning while any of their requests is still undecided.
const LeaveNote: React.FC<{ emp: PayslipEmployee; month: string; leaveDays: number; onUse: () => void }> =
  ({ emp, month, leaveDays, onUse }) => (
    <div className="space-y-2">
      <div className="rounded-xl px-3 py-2.5 text-xs bg-purple-50 text-purple-900">
        {emp.leaveDays
          ? <>Approved leave in {monthTitle(month)}: <b>{emp.leaveDays} day{emp.leaveDays === 1 ? '' : 's'}</b> — {emp.leaveDates.map(fmtLeaveDate).join(', ')}</>
          : <>No approved leave in {monthTitle(month)}.</>}
        {leaveDays !== emp.leaveDays && (
          <button type="button" onClick={onUse} className="ml-2 font-semibold text-purple-700 hover:underline">
            Use approved ({emp.leaveDays})
          </button>
        )}
      </div>
      {emp.pendingLeaveRequests > 0 && (
        <div className="rounded-xl px-3 py-2.5 text-xs bg-amber-50 border border-amber-200 text-amber-800">
          {emp.pendingLeaveRequests === 1 ? '1 leave request' : `${emp.pendingLeaveRequests} leave requests`} in this month
          {emp.pendingLeaveRequests === 1 ? ' is' : ' are'} still waiting —{' '}
          <Link to="/tracker/admin/leave-requests" className="font-semibold underline">approve or reject</Link> first so the leave days are right.
        </div>
      )}
    </div>
  );

// A salesperson's office attendance (Sales CRM → Start work / End work):
// days absent without leave and late days, to weigh up. Nothing is added to
// the leave days unless the admin presses the button.
const AttendanceNote: React.FC<{ emp: PayslipEmployee; month: string; leaveDays: number; onUse: (days: number) => void }> =
  ({ emp, month, leaveDays, onUse }) => {
    const a = emp.attendance;
    if (!a) return null;
    const mins = (m: number) => (m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`);
    const withAbsent = emp.leaveDays + a.absent;
    return (
      <div className={`rounded-xl px-3 py-2.5 text-xs ${a.absent ? 'bg-red-50 text-red-900' : 'bg-gray-50 text-gray-700'}`}>
        <p>Office attendance in {monthTitle(month)} so far: <b>{a.present} day{a.present === 1 ? '' : 's'} present</b>
          {a.late ? <> · {a.late} late ({mins(a.lateMinutes)})</> : null}{a.early ? <> · {a.early} left early</> : null}{a.noEnd ? <> · {a.noEnd} without End work</> : null}</p>
        {a.absent > 0 && (
          <p className="mt-1">
            Absent without leave: <b>{a.absent} day{a.absent === 1 ? '' : 's'}</b> — {a.absentDates.map(d => `${d.label}${d.days < 1 ? ' (½)' : ''}`).join(', ')}
            {leaveDays !== withAbsent && (
              <button type="button" onClick={() => onUse(withAbsent)} className="ml-2 font-semibold text-red-700 hover:underline">
                Count them as leave ({withAbsent})
              </button>
            )}
          </p>
        )}
      </div>
    );
  };

// ── Main Page ─────────────────────────────────────────────────────────────────

const Payslips: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const [initialMonth] = useState(defaultMonth);
  // Active mentors + salespeople, with their approved leave for `month`
  const [staff, setStaff] = useState<{ month: string; list: PayslipEmployee[] }>({ month: '', list: [] });
  const [history, setHistory] = useState<PayslipRecord[]>([]);   // newest first
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormState>({
    employeeType: 'mentor', employeeId: '', email: '', month: initialMonth,
    ...EMPTY_SALARY, totalDays: '', leaveDays: '0',
  });
  // null = use the auto-calculated loss of pay; a string = admin typed an amount
  const [leaveOverride, setLeaveOverride] = useState<string | null>(null);
  const [busy, setBusy] = useState<'preview' | 'send' | null>(null);
  const [historyMonth, setHistoryMonth] = useState('');
  const latestMonth = useRef('');

  // Resolves to null if another month was asked for while this one loaded.
  const fetchEmployees = useCallback(async (month: string) => {
    latestMonth.current = month;
    const res = await getPayslipEmployees(adminPassword, month);
    if (latestMonth.current !== month) return null;
    const list = res.employees ?? [];
    setStaff({ month, list });
    return list;
  }, [adminPassword]);

  const load = useCallback(async (month: string) => {
    setLoading(true);
    try {
      const [, slips] = await Promise.all([fetchEmployees(month), getPayslips(adminPassword)]);
      setHistory(slips.payslips ?? []);
    } catch (err: any) { toast.error('Failed to load: ' + err.message); }
    finally { setLoading(false); }
  }, [adminPassword, fetchEmployees]);

  useEffect(() => { load(initialMonth); }, [load, initialMonth]);

  const set = (patch: Partial<FormState>) => setForm(f => ({ ...f, ...patch }));

  const employees = staff.list;
  const options = employees
    .filter(e => e.employeeType === form.employeeType)
    .sort((a, b) => a.name.localeCompare(b.name));
  const selected = employees.find(e => e.employeeType === form.employeeType && e.employeeId === form.employeeId);
  // Leave figures only count once they're for the month in the form.
  const selectedLeave = selected && staff.month === form.month ? selected : undefined;

  // Same rules as the server's buildPayslip()
  const isSales = form.employeeType === 'sales';
  const calc = useMemo(() => {
    const fixed = rupees(form.basicDa) + rupees(form.hra);
    const incentive = form.employeeType === 'sales' ? rupees(form.incentive) : 0;
    const gross = fixed + incentive;
    const totalDays = num(form.totalDays);
    const leaveDays = num(form.leaveDays);
    const autoLeave = totalDays ? Math.round(fixed / totalDays * leaveDays) : 0;
    const leaveDeduction = !form.deductLeave ? 0 : leaveOverride !== null ? rupees(leaveOverride) : autoLeave;
    const totalDeductions = rupees(form.esic) + rupees(form.epfo) + leaveDeduction;
    return {
      fixed, incentive, gross, autoLeave, leaveDeduction, totalDeductions,
      netSalary: fixed - totalDeductions,
      net: gross - totalDeductions,
      daysWorked: Math.round((totalDays - leaveDays) * 100) / 100,
    };
  }, [form, leaveOverride]);
  const leaveDaysText = `${num(form.leaveDays)} day${num(form.leaveDays) === 1 ? '' : 's'}`;

  const alreadySent = history.find(p =>
    p.employeeType === form.employeeType && p.employeeId === form.employeeId && p.month === form.month);

  // Picking a person pre-fills their salary from their most recent payslip,
  // and their leave days from the month's approved leave.
  const selectEmployee = (employeeId: string) => {
    const emp = employees.find(e => e.employeeType === form.employeeType && e.employeeId === employeeId);
    const last = history.find(p => p.employeeType === form.employeeType && p.employeeId === employeeId);
    setForm(f => ({
      ...f,
      employeeId,
      email: emp?.email ?? '',
      ...EMPTY_SALARY,
      ...(last ? { basicDa: String(last.basicDa), hra: String(last.hra), esic: String(last.esic), epfo: String(last.epfo) } : {}),
      totalDays: f.totalDays || (last ? String(last.totalDays) : ''),
      leaveDays: String(emp && staff.month === f.month ? emp.leaveDays : 0),
    }));
    setLeaveOverride(null);
  };

  // Leave days follow the approved leave of the newly picked month.
  const changeMonth = async (month: string) => {
    set({ month });
    try {
      const list = await fetchEmployees(month);
      if (!list) return;
      setForm(f => {
        const emp = list.find(e => e.employeeType === f.employeeType && e.employeeId === f.employeeId);
        return emp && f.month === month ? { ...f, leaveDays: String(emp.leaveDays) } : f;
      });
      setLeaveOverride(null);
    } catch (err: any) { toast.error('Failed to load leave for that month: ' + err.message); }
  };

  // Load a sent payslip back into the form, e.g. to correct and resend it.
  // It keeps the leave days that were sent; the leave note shows if the
  // approved leave has changed since.
  const loadRecord = (p: PayslipRecord) => {
    if (!employees.some(e => e.employeeType === p.employeeType && e.employeeId === p.employeeId)) {
      toast.error(`${p.name} is no longer active — payslips can only be sent to active mentors and salespeople`);
      return;
    }
    setForm({
      employeeType: p.employeeType, employeeId: p.employeeId, email: p.email, month: p.month,
      basicDa: String(p.basicDa), hra: String(p.hra), incentive: String(p.incentive ?? 0), totalDays: String(p.totalDays),
      leaveDays: String(p.leaveDays), esic: String(p.esic), epfo: String(p.epfo), deductLeave: p.deductLeave !== false,
    });
    const auto = p.totalDays ? Math.round((p.basicDa + p.hra) / p.totalDays * p.leaveDays) : 0;
    setLeaveOverride(p.deductLeave === false || p.leaveDeduction === auto ? null : String(p.leaveDeduction));
    if (p.month !== latestMonth.current) {
      fetchEmployees(p.month).catch((err: any) => toast.error('Failed to load leave for that month: ' + err.message));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const buildInput = (): PayslipInput | null => {
    if (!form.employeeId) { toast.error('Select a mentor or salesperson'); return null; }
    if (!num(form.totalDays)) { toast.error('Enter the working days for the month'); return null; }
    if (num(form.leaveDays) > num(form.totalDays)) { toast.error('Leave days cannot be more than working days'); return null; }
    if (calc.net < 0) { toast.error('Deductions are more than gross earnings'); return null; }
    return {
      employeeType: form.employeeType,
      employeeId: form.employeeId,
      email: form.email.trim(),
      month: form.month,
      basicDa: rupees(form.basicDa),
      hra: rupees(form.hra),
      ...(isSales ? { incentive: rupees(form.incentive) } : {}),
      totalDays: num(form.totalDays),
      leaveDays: num(form.leaveDays),
      esic: rupees(form.esic),
      epfo: rupees(form.epfo),
      deductLeave: form.deductLeave,
      ...(form.deductLeave && leaveOverride !== null ? { leaveDeduction: rupees(leaveOverride) } : {}),
    };
  };

  const handlePreview = async () => {
    const input = buildInput();
    if (!input) return;
    // Open the tab now, while we still have the click — popup blockers stop
    // window.open() calls made after an await.
    const win = window.open('', '_blank');
    setBusy('preview');
    try {
      const res = await previewPayslip(adminPassword, input);
      const bytes = Uint8Array.from(atob(res.pdfBase64), c => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      if (win) win.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: any) { win?.close(); toast.error(err.message); }
    finally { setBusy(null); }
  };

  const handleSend = async () => {
    const input = buildInput();
    if (!input || !selected) return;
    if (!input.email) { toast.error(`Enter an email address for ${selected.name}`); return; }
    const ok = window.confirm(
      `Email the ${monthTitle(input.month)} payslip to ${selected.name} (${input.email})?\n\nNet payable: ${fmt(calc.net)}` +
      (isSales ? ` (salary ${fmt(calc.netSalary)} + incentive ${fmt(calc.incentive)})` : '') +
      (input.leaveDays ? `\nLeave: ${leaveDaysText} — ${input.deductLeave ? `${fmt(calc.leaveDeduction)} deducted` : 'shown, not deducted'}` : '') +
      (alreadySent ? '\n\nA payslip for this month was already sent — this will replace it.' : ''));
    if (!ok) return;
    setBusy('send');
    try {
      const res = await sendPayslip(adminPassword, input);
      toast.success(`Payslip emailed to ${res.sentTo}`);
      setHistory(prev => [res.payslip, ...prev.filter(p => p._id !== res.payslip._id)]);
      // Keep month + working days — the admin usually does everyone in one go.
      set({ employeeId: '', email: '', ...EMPTY_SALARY, leaveDays: '0' });
      setLeaveOverride(null);
    } catch (err: any) { toast.error(err.message); }
    finally { setBusy(null); }
  };

  const historyMonths = Array.from(new Set(history.map(p => p.month))).sort().reverse();
  const shownHistory = historyMonth ? history.filter(p => p.month === historyMonth) : history;

  return (
    <TrackerLayout>
      <div className="space-y-5">
        <div className="rounded-2xl overflow-hidden shadow-lg"
          style={{ background: 'linear-gradient(135deg,#2b2240 0%,#3d2f5f 50%,#2b2240 100%)' }}>
          <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">💸</span>
                <h1 className="text-xl font-bold text-white">Payslips</h1>
              </div>
              <p className="text-white/50 text-xs">Fill in the month's details — the PDF payslip is emailed straight to the mentor or salesperson. Leave days come from approved leave requests, and are only taken off the salary when you tick Deduct leave.</p>
            </div>
            <button onClick={() => load(form.month)}
              className="self-start sm:self-auto px-3 py-2 rounded-xl text-xs font-medium border border-white/20 text-white/70 hover:bg-white/10 transition-colors">
              ↻ Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <div className="w-8 h-8 rounded-full border-3 border-purple-200 border-t-purple-600 animate-spin" style={{ borderWidth: 3 }} />
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-start">
              {/* ── Form ── */}
              <div className="lg:col-span-3 bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
                <SectionTitle>Employee</SectionTitle>
                <div className="grid grid-cols-2 gap-2">
                  {(['mentor', 'sales'] as const).map(t => (
                    <button key={t} type="button"
                      onClick={() => { set({ employeeType: t, employeeId: '', email: '', ...EMPTY_SALARY, leaveDays: '0' }); setLeaveOverride(null); }}
                      className="py-2.5 rounded-xl text-xs font-bold border-2 transition-all"
                      style={form.employeeType === t
                        ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' }
                        : { background: '#f9fafb', color: '#6b7280', borderColor: '#e5e7eb' }}>
                      {t === 'mentor' ? '🎓 Mentor' : '💼 Salesperson'}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Name *">
                    <select value={form.employeeId} onChange={e => selectEmployee(e.target.value)} className={INPUT_CLS + ' bg-white'}>
                      <option value="">— Select {form.employeeType === 'mentor' ? 'mentor' : 'salesperson'} —</option>
                      {options.map(e => (
                        <option key={e.employeeId} value={e.employeeId}>{e.name} ({e.employeeId})</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Pay Month *">
                    <input type="month" value={form.month} onChange={e => e.target.value && changeMonth(e.target.value)} className={INPUT_CLS} />
                  </Field>
                </div>
                <Field label="Send To (Email) *"
                  hint={selected && !selected.email ? 'No email saved for this person — type one here.' : undefined}>
                  <input type="email" value={form.email} onChange={e => set({ email: e.target.value })}
                    placeholder="name@example.com" className={INPUT_CLS} />
                </Field>

                <SectionTitle>Fixed Salary</SectionTitle>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Basic + DA (₹) *">
                    <input type="number" min={0} value={form.basicDa} onChange={e => set({ basicDa: e.target.value })} placeholder="e.g. 7000" className={INPUT_CLS} />
                  </Field>
                  <Field label="HRA (₹)">
                    <input type="number" min={0} value={form.hra} onChange={e => set({ hra: e.target.value })} className={INPUT_CLS} />
                  </Field>
                </div>

                {isSales && (
                  <>
                    <SectionTitle>Incentive</SectionTitle>
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Incentive (₹)" hint="Paid on top of the salary — shown separately on the payslip">
                        <input type="number" min={0} value={form.incentive} onChange={e => set({ incentive: e.target.value })} className={INPUT_CLS} />
                      </Field>
                    </div>
                  </>
                )}

                <SectionTitle>Attendance</SectionTitle>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Working Days in Month *">
                    <input type="number" min={0} step={0.5} value={form.totalDays} onChange={e => set({ totalDays: e.target.value })} placeholder="e.g. 24" className={INPUT_CLS} />
                  </Field>
                  <Field label="Leave Days" hint={num(form.totalDays) ? <>Days worked: <b className="text-gray-600">{calc.daysWorked}</b></> : undefined}>
                    <input type="number" min={0} step={0.5} value={form.leaveDays} onChange={e => set({ leaveDays: e.target.value })} className={INPUT_CLS} />
                  </Field>
                </div>
                {selectedLeave && (
                  <LeaveNote emp={selectedLeave} month={form.month} leaveDays={num(form.leaveDays)}
                    onUse={() => { set({ leaveDays: String(selectedLeave.leaveDays) }); setLeaveOverride(null); }} />
                )}
                {selectedLeave && (
                  <AttendanceNote emp={selectedLeave} month={form.month} leaveDays={num(form.leaveDays)}
                    onUse={days => { set({ leaveDays: String(days) }); setLeaveOverride(null); }} />
                )}

                <SectionTitle>Deductions</SectionTitle>
                <label className="flex items-start gap-3 rounded-xl border-2 px-3 py-2.5 cursor-pointer select-none transition-colors"
                  style={form.deductLeave ? { borderColor: '#c4b5fd', background: '#f5f3ff' } : { borderColor: '#e5e7eb' }}>
                  <input type="checkbox" checked={form.deductLeave}
                    onChange={e => { set({ deductLeave: e.target.checked }); setLeaveOverride(null); }}
                    className="mt-0.5 w-4 h-4 flex-shrink-0 accent-purple-700" />
                  <span>
                    <span className="block text-sm font-semibold text-gray-800">Deduct leave from salary</span>
                    <span className="block text-[11px] text-gray-500 mt-0.5">
                      {form.deductLeave
                        ? 'Per-day salary × leave days is taken off as LEAVE.'
                        : `Leave (${leaveDaysText}) still shows on the payslip — nothing is taken off.`}
                    </span>
                  </span>
                </label>
                <div className={`grid grid-cols-1 gap-4 ${form.deductLeave ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                  <Field label="ESIC (₹)">
                    <input type="number" min={0} value={form.esic} onChange={e => set({ esic: e.target.value })} className={INPUT_CLS} />
                  </Field>
                  <Field label="EPFO (₹)">
                    <input type="number" min={0} value={form.epfo} onChange={e => set({ epfo: e.target.value })} className={INPUT_CLS} />
                  </Field>
                  {form.deductLeave && (
                    <Field label="Leave (₹)"
                      hint={leaveOverride === null
                        ? 'Auto: per-day salary × leave days'
                        : <button type="button" onClick={() => setLeaveOverride(null)} className="text-purple-600 font-semibold hover:underline">
                            Reset to auto ({fmt(calc.autoLeave)})
                          </button>}>
                      <input type="number" min={0} value={leaveOverride ?? String(calc.autoLeave)}
                        onChange={e => setLeaveOverride(e.target.value)} className={INPUT_CLS} />
                    </Field>
                  )}
                </div>
              </div>

              {/* ── Live summary ── */}
              <div className="lg:col-span-2 lg:sticky lg:top-4 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-5 py-4" style={{ background: 'linear-gradient(135deg,#2b2240,#4a3a6a)' }}>
                  <p className="text-white/60 text-[11px] uppercase tracking-wider font-semibold">Pay slip for</p>
                  <p className="text-white font-bold text-base">{monthTitle(form.month)}</p>
                  <p className="text-white/70 text-xs mt-0.5 truncate">{selected ? selected.name : 'No one selected yet'}</p>
                </div>
                <div className="p-5 space-y-2 text-sm">
                  {[
                    ...(isSales ? [['Fixed Salary', fmt(calc.fixed)], ['Incentive', fmt(calc.incentive)]] : []),
                    ['Gross Earnings', fmt(calc.gross)],
                    ['ESIC', fmt(rupees(form.esic))],
                    ['EPFO', fmt(rupees(form.epfo))],
                    [`Leave (${leaveDaysText})`, form.deductLeave || !num(form.leaveDays) ? fmt(calc.leaveDeduction) : 'Not deducted'],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between text-gray-600">
                      <span>{label}</span><span className="font-medium text-gray-800">{value}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-gray-600 border-t border-gray-100 pt-2">
                    <span>Total Deductions</span><span className="font-semibold text-red-600">− {fmt(calc.totalDeductions)}</span>
                  </div>
                  {isSales && (
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <div className="rounded-xl px-3 py-2 bg-gray-50">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Net Salary</p>
                        <p className={`font-bold ${calc.netSalary < 0 ? 'text-red-600' : 'text-gray-800'}`}>{fmt(calc.netSalary)}</p>
                      </div>
                      <div className="rounded-xl px-3 py-2 bg-amber-50">
                        <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wide">Incentive</p>
                        <p className="font-bold text-amber-800">{fmt(calc.incentive)}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline rounded-xl px-4 py-3 mt-2" style={{ background: '#f8f6ff' }}>
                    <span className="text-xs font-bold text-purple-900 uppercase tracking-wide">Net Payable</span>
                    <span className={`text-2xl font-extrabold ${calc.net < 0 ? 'text-red-600' : 'text-purple-900'}`}>{fmt(calc.net)}</span>
                  </div>

                  {alreadySent && (
                    <div className="rounded-xl px-3 py-2.5 text-xs bg-amber-50 border border-amber-200 text-amber-800">
                      Already sent on {new Date(alreadySent.sentAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} to {alreadySent.email} ({fmt(alreadySent.netPay)}).
                      Sending again replaces it and emails the corrected copy.
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button onClick={handlePreview} disabled={busy !== null}
                      className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-60">
                      {busy === 'preview' ? 'Opening…' : '👁 Preview PDF'}
                    </button>
                    <button onClick={handleSend} disabled={busy !== null}
                      className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60"
                      style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)' }}>
                      {busy === 'send' ? 'Sending…' : '📧 Send Payslip'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* ── History ── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-100">
                <p className="font-bold text-gray-800">Sent Payslips <span className="text-gray-400 font-normal text-sm">({shownHistory.length})</span></p>
                <select value={historyMonth} onChange={e => setHistoryMonth(e.target.value)}
                  className="px-3 py-2 border-2 border-gray-200 rounded-xl text-xs bg-white focus:outline-none focus:border-purple-400">
                  <option value="">All months</option>
                  {historyMonths.map(m => <option key={m} value={m}>{monthTitle(m)}</option>)}
                </select>
              </div>
              {shownHistory.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="text-4xl mb-2">📭</div>
                  <p className="text-sm text-gray-400">No payslips sent yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr style={{ background: 'linear-gradient(135deg,#2b2240,#3d2f5f)' }}>
                        {['Name', 'Month', 'Days Worked', 'Leave', 'Salary', 'Incentive', 'Net Pay', 'Sent To', 'Sent On', ''].map(h => (
                          <th key={h} className="px-4 py-3.5 text-left text-xs font-semibold text-white/80 whitespace-nowrap tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {shownHistory.map(p => (
                        <tr key={p._id} className="border-b border-gray-100 hover:bg-purple-50/30 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 whitespace-nowrap">
                              <span className="font-semibold text-gray-800">{p.name}</span>
                              <TypeBadge type={p.employeeType} />
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{monthTitle(p.month)}</td>
                          <td className="px-4 py-3 text-xs text-gray-600">{p.daysWorked} / {p.totalDays}</td>
                          <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                            {p.leaveDays}
                            {p.leaveDays > 0 && (
                              <span className="block text-[10px] text-gray-400">
                                {p.deductLeave ? `− ${fmt(p.leaveDeduction)}` : 'not deducted'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{fmt(p.basicDa + p.hra)}</td>
                          <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                            {p.employeeType === 'sales' ? fmt(p.incentive ?? 0) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-3 font-semibold text-gray-800 whitespace-nowrap">{fmt(p.netPay)}</td>
                          <td className="px-4 py-3 text-xs text-gray-500">{p.email}</td>
                          <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                            {new Date(p.sentAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => loadRecord(p)}
                              className="px-3 py-1.5 rounded-lg text-xs font-bold border-2 whitespace-nowrap"
                              style={{ borderColor: '#3b82f6', color: '#1d4ed8' }}>
                              Edit &amp; Resend
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </TrackerLayout>
  );
};

export default Payslips;
